/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of edits needed to transform one string into another
 */
export const levenshteinDistance = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  const len1 = s1.length;
  const len2 = s2.length;

  const matrix = Array(len2 + 1).fill(null).map(() => Array(len1 + 1).fill(0));

  for (let i = 0; i <= len1; i++) matrix[0][i] = i;
  for (let j = 0; j <= len2; j++) matrix[j][0] = j;

  for (let j = 1; j <= len2; j++) {
    for (let i = 1; i <= len1; i++) {
      const indicator = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + indicator  // substitution
      );
    }
  }

  return matrix[len2][len1];
};

/**
 * Calculate similarity percentage between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity percentage (0-1)
 */
export const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  if (s1 === s2) return 1.0;
  if (!s1.length || !s2.length) return 0.0;

  const maxLength = Math.max(s1.length, s2.length);
  const distance = levenshteinDistance(s1, s2);

  return 1 - (distance / maxLength);
};

/**
 * Extract first and last names from a full name
 * @param {string} fullName - Full name string
 * @returns {Object} - Object with firstName and lastName
 */
const parseFullName = (fullName) => {
  const parts = fullName.trim().split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts[parts.length - 1] || '',
    fullName: fullName.trim()
  };
};

/**
 * Find all matching invitees from the list with similarity scores
 * @param {string} inputName - Name entered by user
 * @param {Array} invitees - List of invited people
 * @param {number} minSimilarity - Minimum similarity threshold (default: 0.7 for 70%)
 * @returns {Array} - Array of matching invitees with similarity scores, sorted by best match
 */
export const findMatchingInvitees = (inputName, invitees, minSimilarity = 0.7) => {
  const input = inputName.toLowerCase().trim();
  const inputParts = input.split(/\s+/);
  const isPartialName = inputParts.length === 1;

  const matches = [];

  for (const invitee of invitees) {
    const { firstName, lastName, fullName } = parseFullName(invitee.name);
    let similarity = 0;

    if (isPartialName) {
      // Single name input - check against both first and last names
      const firstNameSimilarity = calculateSimilarity(input, firstName);
      const lastNameSimilarity = calculateSimilarity(input, lastName);
      similarity = Math.max(firstNameSimilarity, lastNameSimilarity);
    } else {
      // Full name input - check against full name
      similarity = calculateSimilarity(input, fullName);
    }

    if (similarity >= minSimilarity) {
      matches.push({
        ...invitee,
        similarity,
        matchType: isPartialName ? 'partial' : 'full'
      });
    }
  }

  // Sort by similarity (highest first)
  return matches.sort((a, b) => b.similarity - a.similarity);
};

/**
 * Legacy function for backward compatibility
 * Returns single best match or null
 */
export const findMatchingInvitee = (inputName, invitees, maxDistance = 3) => {
  const matches = findMatchingInvitees(inputName, invitees, 0.7);
  return matches.length > 0 ? matches[0] : null;
};
